import { useState } from 'react'
import { useQuoteBuilderApi } from '../api/client'
import { assistantArtifactLinks } from './artifact-links'
import type { AssistantArtifact } from './types'

export function AssistantArtifactLinks({ artifacts }: { artifacts?: AssistantArtifact[] }) {
  const api = useQuoteBuilderApi()
  const [downloading, setDownloading] = useState<string | null>(null)
  const [error, setError] = useState(false)
  const links = assistantArtifactLinks(artifacts)
  if (!links.length) return null
  return (
    <div className="space-y-1">
      <div className="flex flex-wrap gap-x-3 gap-y-1 text-xs font-medium">
        {links.map((link) =>
          link.document && link.href.startsWith('/') ? (
            <button
              className="rounded py-1 underline-offset-4 outline-none hover:underline focus-visible:ring-[3px] focus-visible:ring-ring/50 disabled:opacity-50"
              disabled={downloading !== null}
              key={link.href}
              onClick={async () => {
                setDownloading(link.href)
                setError(false)
                try {
                  const response = await api.fetch(link.href.replace(/^\/dashboard\/quote-builder/, ''))
                  if (!response.ok) throw new Error('Document unavailable')
                  const url = URL.createObjectURL(await response.blob())
                  const anchor = document.createElement('a')
                  anchor.href = url
                  anchor.download = link.label === 'Download invoice' ? 'invoice.pdf' : 'quote.pdf'
                  document.body.append(anchor)
                  anchor.click()
                  anchor.remove()
                  setTimeout(() => URL.revokeObjectURL(url), 60_000)
                } catch {
                  setError(true)
                } finally {
                  setDownloading(null)
                }
              }}
              type="button"
            >
              {downloading === link.href ? 'Downloading...' : link.label}
            </button>
          ) : (
            // eslint-disable-next-line no-restricted-syntax -- a published quote or hosted invoice opens outside the dashboard
            <a
              className="rounded py-1 underline-offset-4 outline-none hover:underline focus-visible:ring-[3px] focus-visible:ring-ring/50"
              href={link.href}
              key={link.href}
              rel="noreferrer"
              target="_blank"
            >
              {link.label}
            </a>
          ),
        )}
      </div>
      {error ? (
        <p className="text-xs text-destructive" role="alert">
          Could not download the document. Try again.
        </p>
      ) : null}
    </div>
  )
}
